import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import productOptionsService from '../services/productOptionsService';
import productsService from '../services/productsService';
import toast from 'react-hot-toast';
import { Plus, Trash2, Save, X, Edit2, Package, Settings, ChevronDown, ChevronRight } from 'lucide-react';
import ConfirmModal from './ConfirmModal';

const ProductOptionsManager = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [options, setOptions] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingOption, setEditingOption] = useState(null);

  const [confirmShow, setConfirmShow] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    produto_id: '',
    nome_opcao: '',
    descricao: '',
    atributos: {
      marcas: [],
      cores: [],
      tamanhos: []
    },
    preco_base: '',
    margem_padrao: '',
    opcoes_detalhadas: [],
    disponivel_apresentacao: true
  });

  // Temporary input states for adding attributes
  const [newMarca, setNewMarca] = useState('');
  const [newCor, setNewCor] = useState('');
  const [newTamanho, setNewTamanho] = useState('');
  const [newDetalhe, setNewDetalhe] = useState({
    marca: '',
    cor: '',
    tamanho: '',
    margem: ''
  });

  useEffect(() => {
    loadProducts();
    loadOptions();
  }, []);

  const loadProducts = async () => {
    try {
      const response = await productsService.getAll();
      if (response.data) {
        setProducts(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    }
  };

  const loadOptions = async () => {
    try {
      setLoading(true);
      const response = await productOptionsService.getAllAvailable();
      if (response.success) {
        setOptions(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar opções:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddAttribute = (type) => {
    const value = type === 'marcas' ? newMarca : type === 'cores' ? newCor : newTamanho;
    if (!value) return;

    setFormData(prev => ({
      ...prev,
      atributos: {
        ...prev.atributos,
        [type]: [...prev.atributos[type], value]
      }
    }));

    if (type === 'marcas') setNewMarca('');
    if (type === 'cores') setNewCor('');
    if (type === 'tamanhos') setNewTamanho('');
  };

  const handleRemoveAttribute = (type, index) => {
    setFormData(prev => ({
      ...prev,
      atributos: {
        ...prev.atributos,
        [type]: prev.atributos[type].filter((_, i) => i !== index)
      }
    }));
  };

  const handleAddDetalhe = () => {
    if (!newDetalhe.margem) return;

    setFormData(prev => ({
      ...prev,
      opcoes_detalhadas: [
        ...prev.opcoes_detalhadas,
        {
          marca: newDetalhe.marca,
          cor: newDetalhe.cor,
          tamanho: newDetalhe.tamanho,
          margem: parseFloat(newDetalhe.margem)
        }
      ]
    }));

    setNewDetalhe({ marca: '', cor: '', tamanho: '', margem: '' });
  };

  const handleRemoveDetalhe = (index) => {
    setFormData(prev => ({
      ...prev,
      opcoes_detalhadas: prev.opcoes_detalhadas.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    if (!formData.produto_id || !formData.nome_opcao) {
      toast.error('Produto e nome da opção são obrigatórios');
      return;
    }

    try {
      setLoading(true);
      const data = {
        ...formData,
        preco_base: formData.preco_base ? parseFloat(formData.preco_base) : null,
        margem_padrao: formData.margem_padrao ? parseFloat(formData.margem_padrao) : 0
      };

      if (editingOption) {
        await productOptionsService.update(editingOption.id, data);
        toast.success('Opção atualizada com sucesso!');
      } else {
        await productOptionsService.create(data);
        toast.success('Opção criada com sucesso!');
      }

      resetForm();
      loadOptions();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar opção');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (option) => {
    setEditingOption(option);
    setFormData({
      produto_id: option.produto_id,
      nome_opcao: option.nome_opcao,
      descricao: option.descricao || '',
      atributos: option.atributos || { marcas: [], cores: [], tamanhos: [] },
      preco_base: option.preco_base || '',
      margem_padrao: option.margem_padrao || '',
      opcoes_detalhadas: option.opcoes_detalhadas || [],
      disponivel_apresentacao: option.disponivel_apresentacao
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    setConfirmMessage('Tem certeza que deseja excluir esta opção?');
    setConfirmAction(() => async () => {
      try {
        await productOptionsService.delete(id);
        toast.success('Opção removida com sucesso!');
        loadOptions();
      } catch (error) {
        console.error('Erro ao excluir:', error);
        toast.error('Erro ao excluir opção');
      }
    });
    setConfirmShow(true);
  };

  const resetForm = () => {
    setFormData({
      produto_id: '',
      nome_opcao: '',
      descricao: '',
      atributos: { marcas: [], cores: [], tamanhos: [] },
      preco_base: '',
      margem_padrao: '',
      opcoes_detalhadas: [],
      disponivel_apresentacao: true
    });
    setNewMarca('');
    setNewCor('');
    setNewTamanho('');
    setNewDetalhe({ marca: '', cor: '', tamanho: '', margem: '' });
    setEditingOption(null);
    setShowForm(false);
  };

  const getProductName = (produtoId) => {
    const product = products.find(p => p.id === produtoId);
    return product ? product.nome : `Produto #${produtoId}`;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Settings className="w-7 h-7" />
            Opções de Produtos para Apresentação
          </h1>
          <p className="text-gray-600 mt-1">
            Configure as opções disponíveis para seleção durante apresentações
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Nova Opção
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                {editingOption ? 'Editar Opção' : 'Nova Opção de Produto'}
              </h2>
              <button onClick={resetForm} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Produto e Nome */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Produto *
                  </label>
                  <select
                    name="produto_id"
                    value={formData.produto_id}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="">Selecione...</option>
                    {products.map(p => (
                      <option key={p.id} value={p.id}>{p.nome}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome da Opção *
                  </label>
                  <input
                    type="text"
                    name="nome_opcao"
                    value={formData.nome_opcao}
                    onChange={handleInputChange}
                    placeholder="Ex: Puxador Perfil"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  name="descricao"
                  value={formData.descricao}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Descrição opcional..."
                />
              </div>

              {/* Preço Base e Margem Padrão */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preço Base (R$)
                  </label>
                  <input
                    type="number"
                    name="preco_base"
                    value={formData.preco_base}
                    onChange={handleInputChange}
                    step="0.01"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Margem Padrão (%)
                  </label>
                  <input
                    type="number"
                    name="margem_padrao"
                    value={formData.margem_padrao}
                    onChange={handleInputChange}
                    step="0.1"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              {/* Marcas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Marcas Disponíveis
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newMarca}
                    onChange={(e) => setNewMarca(e.target.value)}
                    placeholder="Adicionar marca..."
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                  />
                  <button
                    type="button"
                    onClick={() => handleAddAttribute('marcas')}
                    className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.atributos.marcas.map((marca, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                      {marca}
                      <button onClick={() => handleRemoveAttribute('marcas', idx)} className="text-blue-600">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Cores */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cores Disponíveis
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newCor}
                    onChange={(e) => setNewCor(e.target.value)}
                    placeholder="Adicionar cor..."
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                  />
                  <button
                    type="button"
                    onClick={() => handleAddAttribute('cores')}
                    className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.atributos.cores.map((cor, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded text-sm">
                      {cor}
                      <button onClick={() => handleRemoveAttribute('cores', idx)} className="text-purple-600">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Tamanhos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tamanhos Disponíveis
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newTamanho}
                    onChange={(e) => setNewTamanho(e.target.value)}
                    placeholder="Adicionar tamanho..."
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                  />
                  <button
                    type="button"
                    onClick={() => handleAddAttribute('tamanhos')}
                    className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.atributos.tamanhos.map((tamanho, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded text-sm">
                      {tamanho}
                      <button onClick={() => handleRemoveAttribute('tamanhos', idx)} className="text-green-600">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Opções Detalhadas com Margem */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Opções Detalhadas (com margem específica)
                </label>
                <div className="bg-gray-50 p-3 rounded-lg mb-3">
                  <div className="grid grid-cols-5 gap-2">
                    <select
                      value={newDetalhe.marca}
                      onChange={(e) => setNewDetalhe({ ...newDetalhe, marca: e.target.value })}
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value="">Marca</option>
                      {formData.atributos.marcas.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <select
                      value={newDetalhe.cor}
                      onChange={(e) => setNewDetalhe({ ...newDetalhe, cor: e.target.value })}
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value="">Cor</option>
                      {formData.atributos.cores.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select
                      value={newDetalhe.tamanho}
                      onChange={(e) => setNewDetalhe({ ...newDetalhe, tamanho: e.target.value })}
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value="">Tamanho</option>
                      {formData.atributos.tamanhos.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <input
                      type="number"
                      value={newDetalhe.margem}
                      onChange={(e) => setNewDetalhe({ ...newDetalhe, margem: e.target.value })}
                      placeholder="Margem %"
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    />
                    <button
                      type="button"
                      onClick={handleAddDetalhe}
                      className="px-2 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="space-y-1">
                  {formData.opcoes_detalhadas.map((det, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-white border border-gray-200 rounded px-3 py-2 text-sm">
                      <span>
                        {det.marca} + {det.cor} + {det.tamanho} = <strong>{det.margem}%</strong>
                      </span>
                      <button onClick={() => handleRemoveDetalhe(idx)} className="text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Disponibilidade */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="disponivel_apresentacao"
                  checked={formData.disponivel_apresentacao}
                  onChange={handleInputChange}
                  className="rounded border-gray-300"
                />
                <label className="text-sm text-gray-700">
                  Disponível para Apresentação
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {loading ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lista de Opções */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Opções Cadastradas ({options.length})</h2>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500">Carregando...</div>
        ) : options.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Nenhuma opção cadastrada ainda. Clique em "Nova Opção" para começar.
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {options.map(option => (
              <div key={option.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Package className="w-5 h-5 text-gray-400" />
                      <span className="font-medium">{option.nome_opcao}</span>
                      <span className="text-sm text-gray-500">({getProductName(option.produto_id)})</span>
                      {!option.disponivel_apresentacao && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded">
                          Indisponível
                        </span>
                      )}
                    </div>
                    {option.descricao && (
                      <p className="text-sm text-gray-600 mt-1">{option.descricao}</p>
                    )}

                    {/* Atributos */}
                    <div className="flex flex-wrap gap-4 mt-2 text-sm">
                      {option.atributos?.marcas?.length > 0 && (
                        <div>
                          <span className="text-gray-500">Marcas: </span>
                          {option.atributos.marcas.map((m, i) => (
                            <span key={i} className="text-blue-700">{m}{i < option.atributos.marcas.length - 1 ? ', ' : ''}</span>
                          ))}
                        </div>
                      )}
                      {option.atributos?.cores?.length > 0 && (
                        <div>
                          <span className="text-gray-500">Cores: </span>
                          {option.atributos.cores.map((c, i) => (
                            <span key={i} className="text-purple-700">{c}{i < option.atributos.cores.length - 1 ? ', ' : ''}</span>
                          ))}
                        </div>
                      )}
                      {option.atributos?.tamanhos?.length > 0 && (
                        <div>
                          <span className="text-gray-500">Tamanhos: </span>
                          {option.atributos.tamanhos.map((t, i) => (
                            <span key={i} className="text-green-700">{t}{i < option.atributos.tamanhos.length - 1 ? ', ' : ''}</span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Opções Detalhadas */}
                    {option.opcoes_detalhadas?.length > 0 && (
                      <div className="mt-2 text-sm">
                        <span className="text-gray-500">Opções com margem: </span>
                        {option.opcoes_detalhadas.map((d, i) => (
                          <span key={i} className="text-gray-700">
                            {d.marca || '?'}/{d.cor || '?'}/{d.tamanho || '?'} → {d.margem}%{i < option.opcoes_detalhadas.length - 1 ? '; ' : ''}
                          </span>
                        ))}
                      </div>
                    )}

                    {option.preco_base && (
                      <div className="mt-2 text-sm text-gray-600">
                        Preço base: R$ {option.preco_base} | Margem padrão: {option.margem_padrao}%
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(option)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(option.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmModal
        show={confirmShow}
        onConfirm={() => {
          if (confirmAction) confirmAction();
          setConfirmShow(false);
          setConfirmAction(null);
        }}
        onCancel={() => {
          setConfirmShow(false);
          setConfirmAction(null);
        }}
        title="Confirmar Exclusão"
        message={confirmMessage}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  );
};

export default ProductOptionsManager;
